import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea.jsx';
import { Badge } from './ui/badge';
import { Zap, Search, CheckSquare, Square } from 'lucide-react';

const PRIORITY_COLOR = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-slate-100 text-slate-600',
};

const SprintModal = ({ open, sprint, tasks = [], members = [], onClose, onSave, loading }) => {
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [search, setSearch] = useState('');

  const isCreate = !sprint;

  // Unassigned tasks (no sprint yet) — only relevant when creating
  const unassignedTasks = useMemo(() =>
    tasks.filter(t => !t.sprint_id && !['done', 'approved', 'archived'].includes(String(t.status).toLowerCase())),
    [tasks]
  );

  const filteredTasks = useMemo(() =>
    unassignedTasks.filter(t =>
      !search.trim() || t.title.toLowerCase().includes(search.toLowerCase())
    ),
    [unassignedTasks, search]
  );

  useEffect(() => {
    if (sprint) {
      setName(sprint.name || '');
      setGoal(sprint.goal || '');
      setStartDate(sprint.start_date ? sprint.start_date.slice(0, 10) : '');
      setEndDate(sprint.end_date ? sprint.end_date.slice(0, 10) : '');
    } else {
      const today = new Date();
      const twoWeeks = new Date(today);
      twoWeeks.setDate(today.getDate() + 14);
      setName('');
      setGoal('');
      setStartDate(today.toISOString().slice(0, 10));
      setEndDate(twoWeeks.toISOString().slice(0, 10));
    }
    setSelectedTaskIds([]);
    setSearch('');
  }, [sprint, open]);

  const toggleTask = (id) =>
    setSelectedTaskIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  const toggleAll = () =>
    setSelectedTaskIds(
      selectedTaskIds.length === filteredTasks.length ? [] : filteredTasks.map(t => t.id)
    );

  const getMember = (id) => members.find(m => m.user_id === id || m.id === id);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      goal: goal.trim() || null,
      start_date: startDate ? new Date(startDate).toISOString() : null,
      end_date: endDate ? new Date(endDate).toISOString() : null,
      selectedTaskIds: isCreate ? selectedTaskIds : [],
    });
  };

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className={`${isCreate ? 'max-w-2xl' : 'max-w-md'}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#4B1E87]" />
            {sprint ? 'Edit Sprint' : 'Create Sprint'}
          </DialogTitle>
          <DialogDescription>
            {sprint ? 'Update sprint details.' : 'Define a new sprint and assign tasks to it.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {/* Sprint details */}
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Sprint Name <span className="text-rose-500">*</span></label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Sprint 1 — Auth & Onboarding"
                autoFocus
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Sprint Goal</label>
              <Textarea
                value={goal}
                onChange={e => setGoal(e.target.value)}
                placeholder="What should this sprint achieve?"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Start Date</label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">End Date</label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate} />
              </div>
            </div>
          </div>

          {/* Task assignment — only on create */}
          {isCreate && (
            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">
                  Assign Tasks to this Sprint
                  {selectedTaskIds.length > 0 && (
                    <Badge className="ml-2 text-[10px] bg-[#ede0ff] text-[#4B1E87] border-[#d4b8f0]">
                      {selectedTaskIds.length} selected
                    </Badge>
                  )}
                </label>
                {filteredTasks.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="text-xs text-[#4B1E87] hover:underline"
                  >
                    {selectedTaskIds.length === filteredTasks.length ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>

              {unassignedTasks.length === 0 ? (
                <p className="text-xs text-muted-foreground py-3 text-center">All tasks are already in a sprint.</p>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
                    <Input
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search tasks…"
                      className="pl-8 text-xs h-8"
                    />
                  </div>
                  <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                    {filteredTasks.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-3 text-center">No tasks match.</p>
                    ) : filteredTasks.map(task => {
                      const checked = selectedTaskIds.includes(task.id);
                      const member = getMember(task.assignee_id);
                      return (
                        <label
                          key={task.id}
                          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${checked ? 'bg-[#f3eeff]' : 'hover:bg-gray-50'}`}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={checked}
                            onChange={() => toggleTask(task.id)}
                          />
                          {checked
                            ? <CheckSquare className="w-4 h-4 text-[#4B1E87] shrink-0" />
                            : <Square className="w-4 h-4 text-gray-300 shrink-0" />
                          }
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-900 truncate">{task.title}</div>
                            {member && (
                              <div className="text-xs text-gray-400 mt-0.5">{member.full_name || member.email}</div>
                            )}
                          </div>
                          {task.priority && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PRIORITY_COLOR[task.priority] || 'bg-gray-100 text-gray-500'}`}>
                              {task.priority}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading || !name.trim()}>
              {loading
                ? 'Saving…'
                : sprint
                  ? 'Update Sprint'
                  : selectedTaskIds.length > 0
                    ? `Create Sprint & Assign ${selectedTaskIds.length} Task${selectedTaskIds.length !== 1 ? 's' : ''}`
                    : 'Create Sprint'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SprintModal;
